import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/apiClient";
import type { DashboardData } from "@/types/api";
import type { Pc } from "@/types/pc";
import type { User, CurrentUser } from "@/types/user";
import type { Reservation } from "@/types/reservation";
import { StatusCard } from "@/components/StatusCard";
import { LoadingState } from "@/components/LoadingState";
import { EmptyState } from "@/components/EmptyState";
import { ReservationCard } from "@/components/ReservationCard";
import { ReservationModal } from "@/components/ReservationModal";
import { MonthCalendar } from "@/calendar/MonthCalendar";
import { reservationsToEvents } from "@/calendar/calendarUtils";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { todayTokyoDateInput } from "@/lib/date";

export function DashboardPage({ currentUser }: { currentUser: CurrentUser }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [pcs, setPcs] = useState<Pc[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Reservation | null>(null);
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<DashboardData>("/dashboard"),
      api.get<Pc[]>("/pcs"),
      currentUser.role !== "user" ? api.get<User[]>("/users") : Promise.resolve<User[]>([]),
    ])
      .then(([dashboard, pcList, userList]) => {
        setData(dashboard);
        setPcs(pcList);
        setUsers(userList);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [currentUser.role]);

  if (loading) return <LoadingState />;
  if (error || !data) return <EmptyState message={error ?? "データを取得できませんでした。"} />;

  const pcById = new Map(pcs.map((p) => [p.id, p]));
  const userNameById = new Map(users.map((u) => [u.id, u.displayName]));
  if (!userNameById.has(currentUser.id)) userNameById.set(currentUser.id, currentUser.displayName);

  const pcName = (id: number) => pcById.get(id)?.name ?? `PC#${id}`;
  const userName = (id: number) => userNameById.get(id) ?? `利用者#${id}`;

  const events = reservationsToEvents(data.todaysReservations, pcById, userNameById);

  return (
    <div>
      <h1 className="page-title">ダッシュボード</h1>
      <p className="page-subtitle">{todayTokyoDateInput()} の利用状況</p>

      <div className="stat-grid">
        <StatusCard label="利用中" value={data.inUseCount} tone="success" />
        <StatusCard label="本日の利用予定" value={data.todayScheduledCount} tone="primary" />
        <StatusCard label="空きPC" value={data.availablePcCount} />
        <StatusCard label="承認待ち" value={data.pendingApprovalCount} tone="warning" />
        <StatusCard label="返却遅延" value={data.overdueCount} tone="danger" />
      </div>

      <div className="two-col">
        {!isMobile && (
          <div className="card card-padded">
            <h2 className="section-title">月間カレンダー</h2>
            <MonthCalendar
              events={events}
              initialView="dayGridMonth"
              onEventClick={(id) => {
                const r = data.todaysReservations.find((x) => x.id === id);
                if (r) setSelected(r);
              }}
            />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div className="card card-padded">
            <h2 className="section-title">本日の利用一覧</h2>
            {data.todaysReservations.length === 0 ? (
              <EmptyState message="本日の利用予定はありません。" />
            ) : (
              data.todaysReservations.map((r) => (
                <ReservationCard
                  key={r.id}
                  reservation={r}
                  pcName={pcName(r.pc_id)}
                  userName={userName(r.user_id)}
                  onClick={() => setSelected(r)}
                />
              ))
            )}
          </div>

          {currentUser.role !== "user" && (
            <div className="card card-padded">
              <h2 className="section-title">承認待ち一覧</h2>
              {data.pendingApprovals.length === 0 ? (
                <EmptyState message="承認待ちの申請はありません。" />
              ) : (
                data.pendingApprovals.map((r) => (
                  <ReservationCard
                    key={r.id}
                    reservation={r}
                    pcName={pcName(r.pc_id)}
                    userName={userName(r.user_id)}
                    onClick={() => setSelected(r)}
                  />
                ))
              )}
            </div>
          )}

          <button className="btn btn-primary btn-block" onClick={() => navigate("/reservations/new")}>
            新規利用申請
          </button>
        </div>
      </div>

      {selected && (
        <ReservationModal
          reservation={selected}
          pcName={pcName(selected.pc_id)}
          userName={userName(selected.user_id)}
          currentUser={currentUser}
          onClose={() => setSelected(null)}
          onChanged={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
}
